import React, { useMemo } from 'react';
import { compose } from 'redux';
import { withRouter } from 'react-router-dom';
import { connect } from 'react-redux';
import { BlogList, H1, H2, LayoutSingleColumn, NamedLink, Page } from '../../components';
import { isScrollingDisabled } from '../../ducks/ui.duck';
import TopbarContainer from '../TopbarContainer/TopbarContainer';
import FooterContainer from '../FooterContainer/FooterContainer';
import { useIntl } from 'react-intl';

import css from './BlogsPage.module.css';
import classNames from 'classnames';

const BlogsPageComponent = props => {
  const { blogs, getBlogsError, getBlogsInProgress, scrollingDisabled, location } = props;

  const intl = useIntl();

  const { pathname } = location || {};

  const isArtistBlog = pathname.includes('/artist');
  const isClientBlog = pathname.includes('/client');

  const isBasePage = !(isArtistBlog || isClientBlog);

  const title = isBasePage
    ? intl.formatMessage({ id: 'General.blogs' })
    : isArtistBlog
    ? intl.formatMessage({ id: 'General.artistBlogs' })
    : intl.formatMessage({ id: 'General.clientBlogs' });

  const { clientBlogs, artistBlogs } = useMemo(
    () =>
      blogs.reduce(
        (separatedBlogs, blog) => {
          const { assetPath } = blog;

          const isClientBlog = assetPath.startsWith('/content/pages/blogs-client');

          if (isClientBlog) {
            separatedBlogs.clientBlogs.push(blog);
          } else {
            separatedBlogs.artistBlogs.push(blog);
          }

          return separatedBlogs;
        },
        { clientBlogs: [], artistBlogs: [] }
      ),
    [blogs]
  );

  return (
    <Page title={title} scrollingDisabled={scrollingDisabled}>
      <LayoutSingleColumn
        topbar={<TopbarContainer currentPage="ManageListingsPage" />}
        footer={<FooterContainer />}
      >
        <div className={css.root}>
          <H1>{title}</H1>

          <div className={css.blogsSection}>
            {!isArtistBlog && (
              <div>
                {!isClientBlog && <H2>{intl.formatMessage({ id: 'General.clientBlogs' })}</H2>}
                <BlogList
                  blogs={isBasePage ? clientBlogs.slice(0, 3) : clientBlogs}
                  inProgress={getBlogsInProgress}
                  error={getBlogsError}
                  className={classNames(css.blogs, { [css.cutBlogs]: isBasePage })}
                />

                {isBasePage && clientBlogs?.length > 0 && (
                  <NamedLink name="ClientBlogsPage" className={css.cta}>
                    {`${intl.formatMessage({ id: 'General.exploreFurther' })} ››`}
                  </NamedLink>
                )}
              </div>
            )}
            {!isClientBlog && (
              <div>
                {!isArtistBlog && <H2>{intl.formatMessage({ id: 'General.artistBlogs' })}</H2>}
                <BlogList
                  blogs={isBasePage ? artistBlogs.slice(0, 3) : artistBlogs}
                  inProgress={getBlogsInProgress}
                  error={getBlogsError}
                  className={classNames(css.blogs, { [css.cutBlogs]: isBasePage })}
                />
                {isBasePage && artistBlogs?.length > 0 && (
                  <NamedLink name="ArtistBlogsPage" className={css.cta}>
                    {`${intl.formatMessage({ id: 'General.exploreFurther' })} ››`}
                  </NamedLink>
                )}
              </div>
            )}
          </div>
        </div>
      </LayoutSingleColumn>
    </Page>
  );
};

const mapStateToProps = state => {
  const { blogs, getBlogsInProgress, getBlogsError } = state.BlogsPage || {};
  return {
    blogs,
    getBlogsInProgress,
    getBlogsError,
    scrollingDisabled: isScrollingDisabled(state),
  };
};

// Note: it is important that the withRouter HOC is **outside** the
// connect HOC, otherwise React Router won't rerender any Route
// components since connect implements a shouldComponentUpdate
// lifecycle hook.
//
// See: https://github.com/ReactTraining/react-router/issues/4671
const BlogsPage = compose(
  withRouter,
  connect(mapStateToProps)
)(BlogsPageComponent);

export default BlogsPage;
